import { useForm } from '@mantine/form';
import {
  Box,
  Button,
  Flex,
  Group,
  Image,
  LoadingOverlay,
  MultiSelect,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import React, { useRef, useState } from 'react';

import { useBrandList } from '../../../hooks/brands/useBrandList.tsx';
import { ServiceStatus } from '../../../models/PlaceService.ts';
import {
  IsOpenStatus,
  OperationHours,
} from '../../../models/OperationHours.ts';
import {
  IconClock,
  IconPhoto,
  IconPlus,
  IconSettings2,
} from '@tabler/icons-react';
import { dayMap, formatAndValidateTime } from '../../../utils/DateUtil.ts';
import { Dropzone, MIME_TYPES } from '@mantine/dropzone';
import useChargingService from '../../../hooks/charging-services/useChargingService.ts';
import { useChargingServiceImageActions } from '../../../hooks/charging-services/useChargingServiceImageActions.ts';
import { useTagList } from '../../../hooks/tags/useTagList.ts';
import { usePaymentMethodList } from '../../../hooks/payment-methods/usePaymentMethodList.ts';
import { useTheme } from '../../../context/ThemeProvider.tsx';
import { ScrollContainer, ScrollContainerHandle } from '../ScrollContainer.tsx';
import { isAfter, parse } from 'date-fns';
import { JUPITER_BASE_URL, parseStoreUrl } from '../../../utils/storeUrl.ts';
import WeekdayTimeRangeInputs from './WeekdayTimeRangeInputs.tsx';

export interface MutateUpdateChargingServiceFormValues {
  serviceId: string;
  name: string;
  serviceStatus: ServiceStatus | null;
  brandId: string;
  storeIdUrl: string;
  operationHours: OperationHours | null;
  phoneNumberCountryCode: string;
  phoneNumberValue: string;
  website: string;
  notes: string;
  tags: string[];
  paymentMethods: string[];
}

interface MutateUpdateChargingServiceFormProps {
  initialValues: MutateUpdateChargingServiceFormValues;
  onSubmit: (formValues: MutateUpdateChargingServiceFormValues) => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
  submitButtonText?: string;
  isUpdating: boolean;
}

export default function MutateUpdateChargingServiceForm({
  initialValues,
  onSubmit,
  onCancel,
  isSubmitting,
  isUpdating,
}: MutateUpdateChargingServiceFormProps) {
  const form = useForm<MutateUpdateChargingServiceFormValues>({
    mode: 'controlled',
    initialValues: {
      serviceId: initialValues.serviceId,
      name: initialValues.name,
      serviceStatus: initialValues.serviceStatus,
      brandId: initialValues.brandId,
      storeIdUrl: initialValues.storeIdUrl,
      operationHours: initialValues.operationHours,
      phoneNumberCountryCode: initialValues.phoneNumberCountryCode,
      phoneNumberValue: initialValues.phoneNumberValue,
      website: initialValues.website,
      notes: initialValues.notes,
      tags: initialValues.tags,
      paymentMethods: initialValues.paymentMethods,
    },
    validate: (values) => {
      const errors: Record<string, string> = {};

      if (!values.name.trim()) {
        errors['name'] = ' ';
      }

      if (!values.serviceStatus) {
        errors['serviceStatus'] = ' ';
      }

      values.operationHours.weeklySchedule.forEach((day) => {
        if (day.isOpen !== 'OPEN') return;
        if (!day.timeSlots || day.timeSlots.length === 0) return;

        day.timeSlots.forEach((slot, slotIdx) => {
          const ref = new Date(2000, 0, 1);
          const startDate = parse(slot.startTime, 'HH:mm:ss', ref);
          const endDate = parse(slot.endTime, 'HH:mm:ss', ref);

          if (isAfter(startDate, endDate)) {
            errors[
              `operationHours.weeklySchedule.${day.dayOfWeek}.timeSlots.${slotIdx}.endTime`
            ] = ' ';
          }
        });
      });

      if (
        values.phoneNumberValue?.trim() &&
        !values.phoneNumberCountryCode?.trim()
      ) {
        errors['phoneNumberCountryCode'] = ' ';
      }

      if (
        values.phoneNumberCountryCode?.trim() &&
        !values.phoneNumberValue?.trim()
      ) {
        errors['phoneNumberValue'] = ' ';
      }

      if (values.website?.trim()) {
        try {
          new URL(values.website);
        } catch {
          errors['website'] = '無效的 URL 格式';
        }
      }

      if (values.storeIdUrl?.trim()) {
        try {
          parseStoreUrl(values.storeIdUrl, 'CHARGING');
        } catch (error) {
          errors['storeIdUrl'] =
            error instanceof Error ? error.message : '網址格式錯誤';
        }
      }

      return errors;
    },
  });

  const [timeErrors, setTimeErrors] = useState<
    Record<string, { start?: boolean; end?: boolean }>
  >({});

  const commitTime = (
    dayOfWeek: string,
    field: 'start' | 'end',
    raw: string,
  ) => {
    const { formatted, valid } = formatAndValidateTime(raw);
    const timeField = field === 'start' ? 'startTime' : 'endTime';
    if (valid) {
      const schedule = form.values.operationHours.weeklySchedule;
      const idx = schedule.findIndex((w) => w.dayOfWeek === dayOfWeek);
      if (idx !== -1) {
        form.setFieldValue(
          `operationHours.weeklySchedule.${idx}.timeSlots.0.${timeField}`,
          formatted,
        );
      }
      setTimeErrors((prev) => {
        const next = { ...prev[dayOfWeek] };
        delete next[field];
        return { ...prev, [dayOfWeek]: next };
      });
    } else {
      setTimeErrors((prev) => ({
        ...prev,
        [dayOfWeek]: { ...prev[dayOfWeek], [field]: true },
      }));
    }
  };

  const basicInfoRef = useRef<HTMLDivElement>(null);
  const storeUrlRef = useRef<HTMLDivElement>(null);
  const operationHoursRef = useRef<HTMLDivElement>(null);
  const uploadImageRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<ScrollContainerHandle>(null);

  const { brands, isLoading: isBrandListLoading } = useBrandList();
  const { data: tags, isLoading: isTagListLoading } = useTagList();
  const { data: paymentMethods, isLoading: isPaymentMethodListLoading } =
    usePaymentMethodList();
  const { data: chargingService, isLoading: isChargingServiceLoading } =
    useChargingService(initialValues.serviceId);

  const { imageQueries, isAnyImageLoading, handleRemove, handleAdd } =
    useChargingServiceImageActions(chargingService, {
      onAfterAdd: () =>
        scrollContainerRef.current?.scrollToSection(uploadImageRef),
    });

  const handleSubmit = form.onSubmit((values) => {
    onSubmit(values);
  });

  const handleCancel = () => {
    form.reset();
    onCancel?.();
  };

  const { colorScheme } = useTheme();

  if (
    isBrandListLoading ||
    isChargingServiceLoading ||
    isAnyImageLoading ||
    isTagListLoading ||
    isPaymentMethodListLoading
  )
    return <LoadingOverlay visible={false} />;

  const isAllOperationHoursIsOpenUnknown =
    form.values.operationHours?.weeklySchedule.length > 0 &&
    form.values.operationHours?.weeklySchedule.every(
      (w) => w.isOpen === ('UNKNOWN' as IsOpenStatus),
    );

  return (
    <>
      <Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Sticky Tabs */}
        <Group
          gap={0}
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            borderBottom:
              colorScheme === 'light'
                ? '1px solid #dee2e6'
                : '1px solid #737373ff',
            paddingInline: 0,
          }}
        >
          <Button
            variant={'transparent'}
            leftSection={<IconSettings2 size={12} />}
            onClick={() =>
              scrollContainerRef.current?.scrollToSection(basicInfoRef)
            }
            style={{
              fontSize: 14,
              fontWeight: 400,
              width: 100,
              height: 44,
              color: colorScheme === 'light' ? 'black' : 'white',
              paddingInline: 12,
              border: 0,
              borderRadius: 0,
              borderBottom: '2px solid transparent',
            }}
            styles={{
              section: {
                marginRight: 8,
              },
              root: {
                '&:hover': {
                  borderBottom: '2px solid #228be6',
                },
              },
            }}
          >
            基本資訊
          </Button>
          <Button
            variant={'transparent'}
            leftSection={<IconClock size={12} />}
            onClick={() =>
              scrollContainerRef.current?.scrollToSection(storeUrlRef)
            }
            style={{
              fontSize: 14,
              fontWeight: 400,
              width: 100,
              height: 44,
              color: colorScheme === 'light' ? 'black' : 'white',
              paddingInline: 12,
              border: 0,
              borderRadius: 0,
              borderBottom: '2px solid transparent',
            }}
            styles={{
              section: {
                marginRight: 8,
              },
              root: {
                '&:hover': {
                  borderBottom: '2px solid #228be6',
                },
              },
            }}
          >
            營運商店
          </Button>
          <Button
            variant={'transparent'}
            leftSection={<IconClock size={12} />}
            onClick={() =>
              scrollContainerRef.current?.scrollToSection(operationHoursRef)
            }
            style={{
              fontSize: 14,
              fontWeight: 400,
              width: 100,
              height: 44,
              color: colorScheme === 'light' ? 'black' : 'white',
              paddingInline: 12,
              border: 0,
              borderRadius: 0,
              borderBottom: '2px solid transparent',
            }}
            styles={{
              section: {
                marginRight: 8,
              },
              root: {
                '&:hover': {
                  borderBottom: '2px solid #228be6',
                },
              },
            }}
          >
            營業時間
          </Button>
          <Button
            variant={'transparent'}
            leftSection={<IconPhoto size={12} />}
            onClick={() =>
              scrollContainerRef.current?.scrollToSection(uploadImageRef)
            }
            style={{
              fontSize: 14,
              fontWeight: 400,
              width: 100,
              height: 44,
              color: colorScheme === 'light' ? 'black' : 'white',
              paddingInline: 12,
              border: 0,
              borderRadius: 0,
              borderBottom: '2px solid transparent',
            }}
            styles={{
              section: {
                marginRight: 8,
              },
              root: {
                '&:hover': {
                  borderBottom: '2px solid #228be6',
                },
              },
            }}
          >
            上傳圖片
          </Button>
        </Group>

        <ScrollContainer
          ref={scrollContainerRef}
          style={{ paddingInline: 16, paddingTop: 24 }}
        >
          <form id="charging-service-form" onSubmit={handleSubmit}>
            <Stack gap="sm">
              {/* 基本資訊區域 */}
              <div ref={basicInfoRef} style={{ scrollMarginTop: '20px' }}>
                <Group gap={8} style={{ height: 20, marginBottom: 18 }}>
                  <IconSettings2 size={18} />
                  <Text fz={14} fw={400}>
                    基本資訊
                  </Text>
                </Group>

                <Stack gap={'sm'}>
                  <Group gap={16}>
                    {/* 充電服務名稱 */}
                    <TextInput
                      label={'充電服務名稱'}
                      placeholder={'請輸入充電服務名稱'}
                      withAsterisk
                      key={form.key('name')}
                      {...form.getInputProps('name')}
                      error={form.errors['name']}
                      styles={{
                        input: {
                          width: 305.5,
                          borderColor: form.errors.name
                            ? 'var(--mantine-color-red-6)'
                            : undefined,
                        },
                      }}
                    />
                    {/* 服務狀態 */}
                    <Select
                      label={'服務狀態'}
                      placeholder={'請選擇服務狀態'}
                      withAsterisk
                      data={[
                        { label: '服務中', value: 'ONLINE' },
                        { label: '暫停中', value: 'TEMPORARY_OFFLINE' },
                        { label: '停止服務', value: 'DISABLED' },
                      ]}
                      key={form.key('serviceStatus')}
                      {...form.getInputProps('serviceStatus')}
                      error={form.errors['serviceStatus']}
                      styles={{
                        input: {
                          width: 305.5,
                          borderColor: form.errors.serviceStatus
                            ? 'var(--mantine-color-red-6)'
                            : undefined,
                        },
                      }}
                    />
                  </Group>
                  <Group gap={16}>
                    {/* 品牌名稱 */}
                    <Select
                      label={'品牌名稱'}
                      placeholder={'請選擇品牌名稱'}
                      searchable
                      data={brands.map((brand) => ({
                        label: brand.name,
                        value: brand.id,
                      }))}
                      key={form.key('brandId')}
                      {...form.getInputProps('brandId')}
                      styles={{
                        input: {
                          width: 305.5,
                          borderColor: form.errors.brandId
                            ? 'var(--mantine-color-red-6)'
                            : undefined,
                        },
                      }}
                    />
                    <Group gap={0}>
                      <Select
                        label={'服務電話'}
                        placeholder={'請選擇國碼'}
                        data={[
                          { label: '🇹🇼 +886', value: '+886' },
                          { label: '🇯🇵 +81', value: '+81' },
                        ]}
                        key={form.key('phoneNumberCountryCode')}
                        {...form.getInputProps('phoneNumberCountryCode')}
                        error={form.errors['phoneNumberCountryCode']}
                        styles={{
                          wrapper: {
                            width: 108,
                          },
                          input: {
                            width: 108,
                            fontSize: 12,
                            borderColor: form.errors.phoneNumberCountryCode
                              ? 'var(--mantine-color-red-6)'
                              : undefined,
                          },
                          option: {
                            fontSize: 12,
                          },
                        }}
                      />
                      <TextInput
                        label={' '}
                        placeholder={'請輸入服務電話'}
                        key={form.key('phoneNumberValue')}
                        {...form.getInputProps('phoneNumberValue')}
                        error={form.errors['phoneNumberValue']}
                        styles={{
                          input: {
                            width: 197.5,
                            borderColor: form.errors.phoneNumberValue
                              ? 'var(--mantine-color-red-6)'
                              : undefined,
                          },
                        }}
                      />
                    </Group>
                  </Group>
                  {/* 官方網站 */}
                  <TextInput
                    label={'官方網址'}
                    placeholder={'請輸入官方網址'}
                    key={form.key('website')}
                    {...form.getInputProps('website')}
                    styles={{
                      input: {
                        width: 627,
                        borderColor: form.errors.website
                          ? 'var(--mantine-color-red-6)'
                          : undefined,
                      },
                    }}
                  />

                  <MultiSelect
                    label={
                      <Group w={627} justify={'space-between'}>
                        <Text size={'sm'} fw={500}>
                          服務標籤
                        </Text>
                        <Button
                          variant={'outline'}
                          onClick={() => window.open('/tags', '_blank')}
                          styles={{
                            root: {
                              width: 60,
                              height: 15,
                              paddingInline: 6,
                            },
                            label: {
                              fontSize: 10,
                            },
                          }}
                        >
                          管理標籤
                        </Button>
                      </Group>
                    }
                    searchable
                    placeholder={'請選擇服務標籤'}
                    data={tags?.map((tag) => ({
                      label: tag.name,
                      value: tag.id,
                    }))}
                    key={form.key('tags')}
                    {...form.getInputProps('tags')}
                    styles={{
                      input: {
                        width: 627,
                        borderColor: form.errors.tags
                          ? 'var(--mantine-color-red-6)'
                          : undefined,
                      },
                    }}
                  />

                  <MultiSelect
                    label={
                      <Group w={627} justify={'space-between'}>
                        <Text size={'sm'} fw={500}>
                          可用付款方式
                        </Text>
                        <Button
                          variant={'outline'}
                          onClick={() =>
                            window.open('/payment-methods', '_blank')
                          }
                          styles={{
                            root: {
                              width: 100,
                              height: 15,
                              paddingInline: 6,
                            },
                            label: {
                              fontSize: 10,
                            },
                          }}
                        >
                          管理場站付款方式
                        </Button>
                      </Group>
                    }
                    searchable
                    placeholder={'請選擇可用付款方式'}
                    data={paymentMethods?.map((paymentMethod) => ({
                      label: paymentMethod.name,
                      value: paymentMethod.id,
                    }))}
                    key={form.key('paymentMethods')}
                    {...form.getInputProps('paymentMethods')}
                    styles={{
                      input: {
                        borderColor: form.errors.paymentMethods
                          ? 'var(--mantine-color-red-6)'
                          : undefined,
                      },
                    }}
                  />

                  <Textarea
                    label={'備註文字'}
                    placeholder={'請輸入備註文字'}
                    rows={4}
                    key={form.key('notes')}
                    {...form.getInputProps('notes')}
                    styles={{
                      input: {
                        borderColor: form.errors.notes
                          ? 'var(--mantine-color-red-6)'
                          : undefined,
                      },
                    }}
                  />
                </Stack>
              </div>
            </Stack>

            <div
              style={{
                borderBottom:
                  colorScheme === 'light'
                    ? '1px solid #dee2e6'
                    : '1px solid #737373ff',
                marginTop: 32,
                marginBottom: 32,
              }}
            />

            <div ref={storeUrlRef} style={{ scrollMarginTop: '20px' }}>
              <Group gap={8} style={{ height: 20, marginBottom: 12 }}>
                <IconClock size={18} />
                <Text fz={14} fw={400}>
                  營運商店
                </Text>
              </Group>
              <Stack gap={18}>
                {/* 營運商店 */}
                <TextInput
                  label={'營運商店'}
                  placeholder={`${JUPITER_BASE_URL}/chain_stores/{id}/...`}
                  key={form.key('storeIdUrl')}
                  {...form.getInputProps('storeIdUrl')}
                  error={form.errors['storeIdUrl']}
                  styles={{
                    input: {
                      width: 627,
                      borderColor: form.errors.storeIdUrl
                        ? 'var(--mantine-color-red-6)'
                        : undefined,
                    },
                  }}
                />
              </Stack>
            </div>

            <div
              style={{
                borderBottom:
                  colorScheme === 'light'
                    ? '1px solid #dee2e6'
                    : '1px solid #737373ff',
                marginTop: 32,
                marginBottom: 32,
              }}
            />

            <Stack gap={18}>
              {/* 營業時間區域 */}
              <div ref={operationHoursRef} style={{ scrollMarginTop: '20px' }}>
                <Group gap={8} style={{ height: 20, marginBottom: 12 }}>
                  <IconClock size={18} />
                  <Text fz={14} fw={400}>
                    營業時間
                  </Text>
                </Group>
                <Stack gap={18}>
                  <Group justify={'center'}>
                    <Button
                      variant={'outline'}
                      radius={'xl'}
                      color={'#DEE2E6'}
                      onClick={() => {
                        const updated =
                          form.values.operationHours.weeklySchedule.map(
                            (w) => ({
                              ...w,
                              isOpen: 'OPEN' as IsOpenStatus,
                              timeSlots: [
                                {
                                  startTime: '00:00:00',
                                  endTime: '23:59:59',
                                },
                              ],
                            }),
                          );

                        form.setFieldValue(
                          'operationHours.weeklySchedule',
                          updated,
                        );
                      }}
                      style={{
                        width: 104,
                        height: 28,
                        fontSize: 14,
                        fontWeight: 400,
                        color: colorScheme === 'light' ? 'black' : 'white',
                      }}
                      styles={{
                        section: { marginRight: 8 },
                        root: { paddingInline: 8 },
                      }}
                    >
                      24小時營業
                    </Button>
                    <Button
                      variant={'outline'}
                      radius={'xl'}
                      color={'#DEE2E6'}
                      onClick={() => {
                        const updated =
                          form.values.operationHours.weeklySchedule.map((w) => {
                            const isWeekday =
                              w.dayOfWeek === 'MONDAY' ||
                              w.dayOfWeek === 'TUESDAY' ||
                              w.dayOfWeek === 'WEDNESDAY' ||
                              w.dayOfWeek === 'THURSDAY' ||
                              w.dayOfWeek === 'FRIDAY';

                            if (isWeekday) {
                              // 平日：開啟 & 設定 00:00~23:59
                              return {
                                ...w,
                                isOpen: 'OPEN' as IsOpenStatus,
                                timeSlots: [
                                  {
                                    startTime: '00:00:00',
                                    endTime: '23:59:59',
                                  },
                                ],
                              };
                            }

                            // 假日：關閉 & 重置 timeSlots
                            return {
                              ...w,
                              isOpen: 'CLOSED' as IsOpenStatus,
                              timeSlots: [],
                            };
                          });

                        form.setFieldValue(
                          'operationHours.weeklySchedule',
                          updated,
                        );
                      }}
                      style={{
                        width: 104,
                        height: 28,
                        fontSize: 14,
                        fontWeight: 400,
                        color: colorScheme === 'light' ? 'black' : 'white',
                      }}
                      styles={{
                        section: { marginRight: 8 },
                        root: { paddingInline: 8 },
                      }}
                    >
                      平日營業
                    </Button>
                    <Button
                      variant={'outline'}
                      radius={'xl'}
                      color={'#DEE2E6'}
                      onClick={() => {
                        const updated =
                          form.values.operationHours.weeklySchedule.map((w) => {
                            const isWeekend =
                              w.dayOfWeek === 'SATURDAY' ||
                              w.dayOfWeek === 'SUNDAY';

                            if (isWeekend) {
                              // 假日：開啟 & 設定 00:00~23:59
                              return {
                                ...w,
                                isOpen: 'OPEN' as IsOpenStatus,
                                timeSlots: [
                                  {
                                    startTime: '00:00:00',
                                    endTime: '23:59:59',
                                  },
                                ],
                              };
                            }

                            // 平日：關閉 & 重置 timeSlots
                            return {
                              ...w,
                              isOpen: 'CLOSED' as IsOpenStatus,
                              timeSlots: [],
                            };
                          });

                        form.setFieldValue(
                          'operationHours.weeklySchedule',
                          updated,
                        );
                      }}
                      style={{
                        width: 104,
                        height: 28,
                        fontSize: 14,
                        fontWeight: 400,
                        color: colorScheme === 'light' ? 'black' : 'white',
                      }}
                      styles={{
                        section: { marginRight: 8 },
                        root: { paddingInline: 8 },
                      }}
                    >
                      假日營業
                    </Button>
                    <Button
                      variant={'outline'}
                      radius={'xl'}
                      color={'#DEE2E6'}
                      onClick={() => {
                        const updated =
                          form.values.operationHours.weeklySchedule.map(
                            (w) => ({
                              ...w,
                              isOpen: 'UNKNOWN' as IsOpenStatus,
                              timeSlots: [],
                            }),
                          );

                        form.setFieldValue(
                          'operationHours.weeklySchedule',
                          updated,
                        );
                      }}
                      style={{
                        width: 104,
                        height: 28,
                        fontSize: 14,
                        fontWeight: 400,
                        color: colorScheme === 'light' ? 'black' : 'white',
                      }}
                      styles={{
                        section: { marginRight: 8 },
                        root: { paddingInline: 8 },
                      }}
                    >
                      營業時間未知
                    </Button>
                    <Button
                      variant={'outline'}
                      radius={'xl'}
                      color={'#DEE2E6'}
                      onClick={() => {
                        const mondaySchedule =
                          form.values.operationHours.weeklySchedule.find(
                            (w) => w.dayOfWeek === 'MONDAY',
                          );

                        if (!mondaySchedule) return;

                        const updated =
                          form.values.operationHours.weeklySchedule.map((w) => {
                            if (w.dayOfWeek === 'MONDAY') return w;

                            return {
                              ...w,
                              isOpen: mondaySchedule.isOpen,
                              timeSlots: mondaySchedule.timeSlots.map(
                                (slot) => ({ ...slot }),
                              ),
                            };
                          });

                        form.setFieldValue(
                          'operationHours.weeklySchedule',
                          updated,
                        );
                      }}
                      style={{
                        width: 104,
                        height: 28,
                        fontSize: 14,
                        fontWeight: 400,
                        color: colorScheme === 'light' ? 'black' : 'white',
                      }}
                      styles={{
                        section: { marginRight: 8 },
                        root: { paddingInline: 8 },
                      }}
                    >
                      套用週一設定
                    </Button>
                  </Group>

                  <Stack gap={16}>
                    {isAllOperationHoursIsOpenUnknown ? (
                      <div
                        style={{
                          backgroundColor:
                            colorScheme === 'light' ? '#F8F9FA' : '#2E2E2E',
                          width: 627,
                          height: 100,
                          borderRadius: 4,
                          padding: 16,
                          color: 'grey',
                          alignContent: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Group justify={'center'}>
                          <Text style={{ fontSize: 14 }}>營業時間未知</Text>
                        </Group>
                      </div>
                    ) : (
                      Object.entries(dayMap).map(([key, { label }]) => (
                        <div
                          key={key}
                          style={{
                            backgroundColor:
                              colorScheme === 'light' ? '#F8F9FA' : '#2E2E2E',
                            width: 627,
                            height: 55,
                            borderRadius: 4,
                            padding: 16,
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Group align={'center'}>
                            <div
                              style={{
                                fontSize: 14,
                                fontWeight: 400,
                                lineHeight: '22px',
                                color:
                                  form.values.operationHours?.weeklySchedule.find(
                                    (w) => w.dayOfWeek === key,
                                  ).isOpen === 'OPEN'
                                    ? colorScheme === 'light'
                                      ? 'black'
                                      : 'white'
                                    : 'grey',
                              }}
                            >
                              {label}
                            </div>
                            {form.values.operationHours?.weeklySchedule
                              .find((w) => w.dayOfWeek === key)
                              ?.isOpen.toString() === 'OPEN' && (
                              <WeekdayTimeRangeInputs
                                startValue={
                                  form.values.operationHours?.weeklySchedule
                                    .find((w) => w.dayOfWeek === key)
                                    ?.timeSlots[0]?.startTime.split(':')
                                    .slice(0, 2)
                                    .join(':') ?? '00:00'
                                }
                                endValue={
                                  form.values.operationHours?.weeklySchedule
                                    .find((w) => w.dayOfWeek === key)
                                    ?.timeSlots[0]?.endTime.split(':')
                                    .slice(0, 2)
                                    .join(':') ?? '23:59'
                                }
                                onStartChange={(value) => {
                                  const schedule =
                                    form.values.operationHours.weeklySchedule;
                                  const idx = schedule.findIndex(
                                    (w) => w.dayOfWeek === key,
                                  );
                                  if (idx === -1) return;
                                  form.setFieldValue(
                                    `operationHours.weeklySchedule.${idx}.timeSlots.0.startTime`,
                                    value,
                                  );
                                }}
                                onEndChange={(value) => {
                                  const schedule =
                                    form.values.operationHours.weeklySchedule;
                                  const idx = schedule.findIndex(
                                    (w) => w.dayOfWeek === key,
                                  );
                                  if (idx === -1) return;
                                  form.setFieldValue(
                                    `operationHours.weeklySchedule.${idx}.timeSlots.0.endTime`,
                                    value,
                                  );
                                }}
                                onCommitStart={(value) =>
                                  commitTime(key, 'start', value)
                                }
                                onCommitEnd={(value) =>
                                  commitTime(key, 'end', value)
                                }
                                startError={!!timeErrors[key]?.start}
                                endError={
                                  !!timeErrors[key]?.end ||
                                  !!form.errors[
                                    `operationHours.weeklySchedule.${key}.timeSlots.0.endTime`
                                  ]
                                }
                              />
                            )}
                          </Group>

                          <div style={{ height: 24, alignContent: 'center' }}>
                            <Switch
                              checked={
                                form.values.operationHours?.weeklySchedule.find(
                                  (w) => w.dayOfWeek === key,
                                )?.isOpen === 'OPEN'
                              }
                              onChange={(e) => {
                                const checked = e.currentTarget.checked;

                                const idx =
                                  form.values.operationHours.weeklySchedule.findIndex(
                                    (w) => w.dayOfWeek === key,
                                  );

                                if (idx === -1) return;

                                if (!checked) {
                                  form.setFieldValue(
                                    `operationHours.weeklySchedule.${idx}`,
                                    {
                                      ...form.values.operationHours
                                        .weeklySchedule[idx],
                                      isOpen: 'CLOSED',
                                      timeSlots: [],
                                    },
                                  );
                                } else {
                                  form.setFieldValue(
                                    `operationHours.weeklySchedule.${idx}`,
                                    {
                                      ...form.values.operationHours
                                        .weeklySchedule[idx],
                                      isOpen: 'OPEN',
                                      timeSlots: [
                                        {
                                          startTime: '00:00:00',
                                          endTime: '23:59:00',
                                        },
                                      ],
                                    },
                                  );
                                }
                              }}
                            />
                          </div>
                        </div>
                      ))
                    )}
                  </Stack>
                </Stack>
              </div>
            </Stack>

            <div
              style={{
                borderBottom:
                  colorScheme === 'light'
                    ? '1px solid #dee2e6'
                    : '1px solid #737373ff',
                marginTop: 32,
                marginBottom: 32,
              }}
            />

            {/* 上傳圖片區域 */}
            <div
              ref={uploadImageRef}
              style={{ scrollMarginTop: '20px', marginBottom: 24 }}
            >
              <Group gap={8} style={{ height: 20, marginBottom: 18 }}>
                <IconPhoto size={18} />
                <Text fz={14} fw={400}>
                  上傳圖片
                </Text>
              </Group>
              <Flex
                gap={9}
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  overflowX: 'hidden',
                  overflowY: 'auto',
                  alignContent: 'flex-start',
                }}
              >
                {imageQueries?.map((q) => (
                  <div
                    key={q.data.imageId}
                    style={{
                      width: 150,
                      height: 150,
                      borderRadius: 8,
                      position: 'relative',
                      overflow: 'hidden',
                      flexShrink: 0,
                    }}
                  >
                    <Image
                      src={q.data.signedUrl}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                    {/* 刪除按鈕 */}
                    <Button
                      size="xs"
                      variant="filled"
                      color="red"
                      style={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        padding: '2px 6px',
                        zIndex: 2,
                      }}
                      onClick={() => handleRemove(q)}
                    >
                      ×
                    </Button>
                  </div>
                ))}

                {/* 新增圖片按鈕 */}
                <div
                  style={{
                    width: 150,
                    height: 150,
                    borderRadius: 8,
                    backgroundColor: '#F8F9FA',
                    alignContent: 'center',
                    flexShrink: 0,
                    cursor: 'pointer',
                  }}
                >
                  <Dropzone
                    onDrop={(files) => handleAdd(files)}
                    onReject={(files) => console.log('rejected files', files)}
                    maxSize={5 * 1024 ** 2}
                    accept={[MIME_TYPES.png]}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: '1px dashed var(--mantine-color-gray-4)',
                      borderRadius: 8,
                      padding: 0,
                    }}
                    styles={{
                      inner: {
                        height: '100%',
                      },
                    }}
                  >
                    <Group justify="center" style={{ height: '100%' }}>
                      <Stack align="center">
                        <IconPlus size={24} color="#868E96" />
                        <Text
                          style={{
                            fontSize: 16,
                            fontWeight: 500,
                            color: '#868E96',
                          }}
                        >
                          新增圖片
                        </Text>
                      </Stack>
                    </Group>
                  </Dropzone>
                </div>
              </Flex>
            </div>
          </form>
        </ScrollContainer>

        {/* 取消 與 新增 or 更新按鈕 */}
        <Group
          justify="flex-end"
          style={{
            height: 70,
            paddingInline: 16,
            borderTop:
              colorScheme === 'light'
                ? '1px solid #dee2e6'
                : '1px solid #737373ff',
          }}
        >
          <Button variant="outline" onClick={handleCancel}>
            取消
          </Button>
          <Button
            type={'submit'}
            form="charging-service-form" // 關聯到 form
            loading={isSubmitting}
            loaderProps={{ type: 'oval', size: 'xs' }}
          >
            {isUpdating ? '更新' : '下一步'}
          </Button>
        </Group>
      </Box>
    </>
  );
}
